import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import UnifiedPropertySearch from './pages/UnifiedPropertySearch';
import PropertyDetailV2 from './pages/PropertyDetailV2';
import TenantDashboard from './pages/TenantDashboard';
import TenantViewings from './pages/TenantViewings';
import LandlordDashboard from './pages/LandlordDashboard';
import Applications from './pages/Applications';
import MyTenancies from './pages/MyTenancies';
import Maintenance from './pages/Maintenance';
import Properties from './pages/Properties';
import Payments from './pages/Payments';
import Documents from './pages/Documents';
import Notifications from './pages/Notifications';
import Compliance from './pages/Compliance';
import Automation from './pages/Automation';
import AIChat from './pages/AIChat';
import ForTenants from './pages/ForTenants';
import ForLandlords from './pages/ForLandlords';
import ForAgents from './pages/ForAgents';
import LandlordOnboardingSimple from './pages/LandlordOnboardingSimple';
import TenantOnboardingSimple from './pages/TenantOnboardingSimple';
import AuthCallback from './components/AuthCallback';
import VaaSLandlordPage from './pages/public/VaaSLandlordPage';
import VaaSTenantPage from './pages/public/VaaSTenantPage';
import VaaSAdminCalendar from './pages/VaaSAdminCalendar';
import AdminConfig from './pages/AdminConfig';
import RepairTracking from './pages/RepairTracking';
import ContractorDashboard from './pages/ContractorDashboard';
import ContractorJobOffers from './pages/ContractorJobOffers';
import TermsOfBusiness from './pages/TermsOfBusiness';
import ComplaintsProcedure from './pages/ComplaintsProcedure';
import ContractorActiveJobs from './pages/ContractorActiveJobs';
import ContractorCompletedJobs from './pages/ContractorCompletedJobs';
import ContractorJobDetails from './pages/ContractorJobDetails';
import ContractorAccount from './pages/ContractorAccount';
import AdminConsole from './pages/AdminConsole';
import AdminUsers from './pages/AdminUsers';
import AdminPropertiesList from './pages/AdminPropertiesList';
import AdminTenancies from './pages/AdminTenancies';
import PropertyWizard from './pages/PropertyWizard';
import TenantInspections from './pages/TenantInspections';
import LandlordStatements from './pages/LandlordStatements';
import AdminSimulation from './pages/AdminSimulation';
import LandlordDeposits from './pages/LandlordDeposits';
import AdminInspectionWorkflow from './pages/AdminInspectionWorkflow';
import LandlordVerification from './pages/LandlordVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AISupport from './pages/AISupport';
import RentersRightsActPage from './pages/RentersRightsActPage';

// Blog Pages
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';

// Floating Components
import ComplianceFloatingBanner from './components/ComplianceFloatingBanner';
import CookieConsent from './components/CookieConsent';
import MyCases from './pages/MyCases';
import AdminCases from './pages/AdminCases';
import AdminKnowledgeBase from './pages/AdminKnowledgeBase';
import AdminArrears from './pages/AdminArrears';
import AdminNotificationTemplates from './pages/AdminNotificationTemplates';
import AIReviewQueue from './pages/AIReviewQueue';
import AIPolicyMatrix from './pages/AIPolicyMatrix';
import AdminImpersonation from './pages/AdminImpersonation';
import TenancyLifecycle from './pages/TenancyLifecycle';
import AIAgentsDashboard from './pages/AIAgentsDashboard';
import HowItWorks from './pages/HowItWorks';
import CompliancePage from './pages/CompliancePage';
import InspectionsPage from './pages/InspectionsPage';
import RoomManagement from './pages/RoomManagement';
import HMOCompliance from './pages/HMOCompliance';
import PropertyTypeSettings from './pages/PropertyTypeSettings';
import ScottishDepositExport from './pages/ScottishDepositExport';
import DocuSignTemplateAdmin from './pages/DocuSignTemplateAdmin';
import GriffinAdminDashboard from './pages/GriffinAdminDashboard';
import LandlordBankingOverview from './pages/LandlordBankingOverview';
import LandlordFinancialDashboard from './pages/LandlordFinancialDashboard';
import FiscalAutomationDashboard from './pages/FiscalAutomationDashboard';

// Scanning & Inspection Pages
import BaselineScan from './pages/BaselineScan';
import TenantInspectionScan from './pages/TenantInspectionScan';
import ScanResults from './pages/ScanResults';
import ScanSettings from './pages/ScanSettings';

// Tenancy Lifecycle Pages
import JointTenancy from './pages/JointTenancy';
import GuarantorVerification from './pages/GuarantorVerification';
import MoveOutChecklist from './pages/MoveOutChecklist';
import InventoryManagement from './pages/InventoryManagement';
import RentEvictionDashboard from './pages/RentEvictionDashboard';
import DirectDebitManagement from './pages/DirectDebitManagement';

// Security & MFA
import AdminSecuritySettings from './pages/AdminSecuritySettings';
import UserProfile from './pages/UserProfile';

// Autonomy System Pages
import {
  AutonomyDashboard,
  AutonomyAgents,
  AutonomyTasks,
  AutonomyEvents,
  AutonomyModels,
  AutonomyCaseMemory,
  AutonomyExplainability,
  AgentLearningDashboard,
  GovernanceDashboard
} from './pages/admin';

// Inventory Management - Landlord
import InventoryCreate from './pages/landlords/InventoryCreate';

// Tenancy Management
import TenancyDetail from './pages/landlords/TenancyDetail';

// Finance Operations Pages
import { FinanceDashboard, FinanceSchedules, FinanceRuns, DepositLifecycle, CreditReporting } from './pages/finance';

// New Public Pages - Landlords
import { 
  LandlordsOverview, 
  LandlordsListing, 
  LandlordsCompliance, 
  LandlordsTenancy, 
  LandlordsMaintenance, 
  LandlordsPricing, 
  LandlordsFAQs 
} from './pages/landlords';

// New Public Pages - Tenants
import { 
  TenantsOverview, 
  TenantsFindApply, 
  TenantsReferencing, 
  TenantsJourney, 
  TenantsMoving, 
  TenantsFAQs 
} from './pages/tenants';

// New Public Pages - Contractors
import { 
  ContractorsOverview, 
  ContractorsGettingStarted, 
  ContractorsJobs, 
  ContractorsPayments 
} from './pages/contractors';

// New Public Pages - Agents
import AgentsPage from './pages/AgentsPage';

// RRA 2025 Compliance Pages
import {
  ComplianceDashboard as RRAComplianceDashboard,
  PetRequestsPage,
  ListingScannerPage
} from './pages/compliance';

// PRS Services Pages - P1/P2 Features
import PRSExportPage from './pages/PRSExportPage';
import OmbudsmanCasesPage from './pages/OmbudsmanCasesPage';
import RepairsSLAPage from './pages/RepairsSLAPage';
import SavedSearchesPage from './pages/SavedSearchesPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  // Check for session_id in URL fragment synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/properties" element={<UnifiedPropertySearch />} />
      <Route path="/properties/:id" element={<PropertyDetailV2 />} />
      <Route path="/search" element={<UnifiedPropertySearch />} />
      <Route path="/listings" element={<UnifiedPropertySearch />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Role-specific landing pages */}
      <Route path="/for-tenants" element={<ForTenants />} />
      <Route path="/for-landlords" element={<ForLandlords />} />
      <Route path="/for-agents" element={<ForAgents />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/inspections" element={<InspectionsPage />} />
      
      {/* New Public Pages - Landlords */}
      <Route path="/landlords" element={<LandlordsOverview />} />
      <Route path="/landlords/listing" element={<LandlordsListing />} />
      <Route path="/landlords/compliance" element={<LandlordsCompliance />} />
      <Route path="/landlords/tenancy" element={<LandlordsTenancy />} />
      <Route path="/landlords/maintenance" element={<LandlordsMaintenance />} />
      <Route path="/landlords/pricing" element={<LandlordsPricing />} />
      <Route path="/landlords/faqs" element={<LandlordsFAQs />} />
      <Route path="/landlords/viewings-as-a-service" element={<VaaSLandlordPage />} />
      
      {/* Legal Pages */}
      <Route path="/terms-of-business" element={<TermsOfBusiness />} />
      <Route path="/complaints" element={<ComplaintsProcedure />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/contact" element={<Contact />} />
      
      {/* Renters Rights Act 2025 Information */}
      <Route path="/renters-rights-act" element={<RentersRightsActPage />} />
      
      {/* Blog */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      
      {/* New Public Pages - Tenants */}
      <Route path="/tenants" element={<TenantsOverview />} />
      <Route path="/tenants/find-apply" element={<TenantsFindApply />} />
      <Route path="/tenants/referencing" element={<TenantsReferencing />} />
      <Route path="/tenants/journey" element={<TenantsJourney />} />
      <Route path="/tenants/moving" element={<TenantsMoving />} />
      <Route path="/tenants/faqs" element={<TenantsFAQs />} />
      <Route path="/tenants/viewings" element={<VaaSTenantPage />} />
      
      {/* New Public Pages - Contractors */}
      <Route path="/contractors" element={<ContractorsOverview />} />
      <Route path="/contractors/getting-started" element={<ContractorsGettingStarted />} />
      <Route path="/contractors/jobs" element={<ContractorsJobs />} />
      <Route path="/contractors/payments" element={<ContractorsPayments />} />
      <Route path="/contractor/register" element={<Register />} />
      
      {/* New Public Pages - Agents */}
      <Route path="/agents" element={<AgentsPage />} />
      
      {/* RRA 2025 Compliance Routes */}
      <Route
        path="/compliance/rra"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'admin', 'super_admin']}>
            <RRAComplianceDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance/pet-requests"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'admin', 'super_admin', 'tenant']}>
            <PetRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance/listing-scan"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'admin', 'super_admin']}>
            <ListingScannerPage />
          </ProtectedRoute>
        }
      />

      {/* PRS Services - P1/P2 Features */}
      <Route
        path="/prs/export"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'admin', 'super_admin']}>
            <PRSExportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prs/ombudsman"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'tenant', 'admin', 'super_admin']}>
            <OmbudsmanCasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prs/repairs"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'tenant', 'contractor', 'admin', 'super_admin']}>
            <RepairsSLAPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-searches"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'tenant', 'admin', 'super_admin']}>
            <SavedSearchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/saved-searches"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <SavedSearchesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Onboarding */}
      <Route
        path="/landlord/onboarding"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <LandlordOnboardingSimple />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/onboarding"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <TenantOnboardingSimple />
          </ProtectedRoute>
        }
      />
      
      {/* Contractor Routes */}
      <Route
        path="/contractor/dashboard"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/job-offers"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorJobOffers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/active-jobs"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorActiveJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/completed-jobs"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorCompletedJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/jobs/:jobId"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorJobDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/account"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorAccount />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/console"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminConsole />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/properties"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminPropertiesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenancies"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminTenancies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/config"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/simulation"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminSimulation />
          </ProtectedRoute>
        }
      />
      
      {/* Repairs */}
      <Route
        path="/repairs"
        element={
          <ProtectedRoute>
            <RepairTracking />
          </ProtectedRoute>
        }
      />
      
      {/* Tenant Routes */}
      <Route
        path="/tenant/dashboard"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <TenantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/viewings"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <TenantViewings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/inspections"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <TenantInspections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/applications"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <Applications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/tenancies"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <MyTenancies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/maintenance"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <Maintenance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/payments"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <Payments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/documents"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/notifications"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <Notifications />
          </ProtectedRoute>
        }
      />

      {/* Landlord Routes */}
      <Route
        path="/landlord/dashboard"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <LandlordDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Properties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/applications"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Applications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/tenancies"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <MyTenancies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/tenancies/:tenancyId"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'admin', 'super_admin']}>
            <TenancyDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/maintenance"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Maintenance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/payments"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Payments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/compliance"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Compliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/ai-chat"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <AIChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/automation"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Automation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/statements"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <LandlordStatements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/banking"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <LandlordBankingOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/financials"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'admin', 'super_admin']}>
            <LandlordFinancialDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties/new"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <PropertyWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties/:propertyId"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <PropertyDetailV2 mode="landlord" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties/:propertyId/rooms"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <RoomManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties/:propertyId/hmo-compliance"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <HMOCompliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties/:propertyId/type-settings"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <PropertyTypeSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/documents"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/notifications"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/deposits"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <LandlordDeposits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/verification"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <LandlordVerification />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/inventory/create"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <InventoryCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/properties/:propertyId/inventory/create"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <InventoryCreate />
          </ProtectedRoute>
        }
      />
      {/* Admin Inspection Workflow */}
      <Route
        path="/admin/inspection-workflow"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminInspectionWorkflow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cases"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent', 'admin']}>
            <AdminCases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/docusign-templates"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <DocuSignTemplateAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/griffin"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <GriffinAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fiscal-automation"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <FiscalAutomationDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/knowledge-base"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AdminKnowledgeBase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/arrears"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AdminArrears />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AdminNotificationTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-review"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AIReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-policy"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AIPolicyMatrix />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-agents"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent']}>
            <AIAgentsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/impersonate"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminImpersonation />
          </ProtectedRoute>
        }
      />
      {/* VaaS Admin Calendar */}
      <Route
        path="/admin/vaas-calendar"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <VaaSAdminCalendar />
          </ProtectedRoute>
        }
      />
      {/* Admin Autonomy Dashboard Routes (Admin-Only) */}
      <Route
        path="/admin/autonomy"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agent-learning"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AgentLearningDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autonomy/events"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autonomy/tasks"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyTasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autonomy/agents"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyAgents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autonomy/models"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyModels />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autonomy/case-memory"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyCaseMemory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autonomy/explainability"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AutonomyExplainability />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/governance"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <GovernanceDashboard />
          </ProtectedRoute>
        }
      />
      {/* Admin Security & MFA Settings */}
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AdminSecuritySettings />
          </ProtectedRoute>
        }
      />
      {/* User Profile & Settings - All authenticated users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['tenant', 'landlord', 'landlord_admin', 'admin', 'super_admin', 'contractor', 'support_agent']}>
            <UserProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['tenant', 'landlord', 'landlord_admin', 'admin', 'super_admin', 'contractor', 'support_agent']}>
            <UserProfile />
          </ProtectedRoute>
        }
      />
      {/* INTERNAL ONLY - AI Automation (Admin access only) */}
      <Route
        path="/internal/automation"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent']}>
            <Automation />
          </ProtectedRoute>
        }
      />
      {/* INTERNAL ONLY - AI Chat (Admin access only) */}
      <Route
        path="/internal/ai-chat"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'support_agent']}>
            <AIChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/ai-chat"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <AIChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/my-cases"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <MyCases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/my-cases"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin']}>
            <MyCases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenancy/:tenancyId/lifecycle"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'tenant', 'super_admin', 'support_agent']}>
            <TenancyLifecycle />
          </ProtectedRoute>
        }
      />

      {/* Finance Operations Routes */}
      <Route
        path="/admin/finance"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'landlord_admin']}>
            <FinanceDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finance/schedules"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'landlord_admin']}>
            <FinanceSchedules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finance/runs"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'landlord_admin']}>
            <FinanceRuns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/deposits"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'landlord', 'landlord_admin']}>
            <DepositLifecycle />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scottish-deposits"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ScottishDepositExport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/credit-reporting"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <CreditReporting />
          </ProtectedRoute>
        }
      />

      {/* Scanning & Inspection Routes */}
      <Route
        path="/scan/baseline/:propertyId"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'super_admin', 'admin']}>
            <BaselineScan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan/inspection/:propertyId"
        element={
          <ProtectedRoute allowedRoles={['tenant', 'landlord', 'landlord_admin', 'super_admin', 'admin']}>
            <TenantInspectionScan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan/results/:sessionId"
        element={
          <ProtectedRoute>
            <ScanResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan/settings"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'super_admin', 'admin']}>
            <ScanSettings />
          </ProtectedRoute>
        }
      />

      {/* Tenancy Lifecycle Routes */}
      <Route
        path="/tenant/joint-tenancy"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <JointTenancy />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guarantor/verify"
        element={<GuarantorVerification />}
      />
      <Route
        path="/tenant/move-out/:checklistId"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <MoveOutChecklist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/:inventoryId"
        element={
          <ProtectedRoute allowedRoles={['tenant', 'landlord', 'landlord_admin', 'super_admin', 'admin']}>
            <InventoryManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/rent-eviction"
        element={
          <ProtectedRoute allowedRoles={['landlord', 'landlord_admin', 'super_admin', 'admin']}>
            <RentEvictionDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/direct-debit"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <DirectDebitManagement />
          </ProtectedRoute>
        }
      />

      <Route path="/unauthorized" element={<div className="min-h-screen flex items-center justify-center"><h1 className="text-2xl">Unauthorized Access</h1></div>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <ComplianceFloatingBanner />
            <CookieConsent />
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
